import React from 'react';
import { useRouteError, useNavigate, isRouteErrorResponse } from 'react-router-dom';
import Button from '../../components/Reusable/Button';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

const AdminErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const getErrorDetails = () => {
    if (isRouteErrorResponse(error)) {
      return {
        title: `${error.status} Error`,
        message: error.statusText || error.data?.message || 'An error occurred',
      };
    }

    if (error instanceof Error) {
      if (error.message?.includes('not found in componentMap')) {
        return {
          title: 'Component Error',
          message: 'The requested page is not properly configured.',
        };
      }

      return {
        title: 'Something went wrong',
        message: 'An unexpected error occurred. Please try again.',
      };
    }

    return {
      title: 'Error',
      message: 'An unexpected error occurred',
    };
  };

  const errorDetails = getErrorDetails();

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-[var(--color-background-09)] flex items-center justify-center">
            <AlertTriangle className="w-10 h-10 text-[var(--color-error)]" />
          </div>
        </div>

        <div className="bg-[var(--color-background-01)] rounded-2xl border border-[var(--color-stroke-01)] p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-[var(--color-content)] mb-3">
            {errorDetails.title}
          </h1>
          
          <p className="text-[var(--color-content-03)] text-lg leading-relaxed mb-8">
            {errorDetails.message}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              buttonText="Try Again"
              variant="secondary"
              onClick={() => window.location.reload()}
              icon={<RefreshCw size={16} />}
              iconPosition="left"
              className="flex-1 sm:flex-none text-[14px]"
            />
            <Button
              buttonText="Go to Dashboard"
              variant="primary"
              onClick={() => navigate('/admin/dashboard')}
              icon={<Home size={16} />}
              iconPosition="left"
              className="flex-1 sm:flex-none text-[14px]"
            />
          </div>
        </div>

        <p className="text-[var(--color-content-03)] text-sm mt-6">
          If the problem continues, contact support
        </p>
      </div>
    </div>
  );
};

export default AdminErrorBoundary;